import React, { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BoxPage } from '@/components/common'
import { Card } from '@/@core/components/mui/Card'
import { CardContent, IconButton, Typography, Box, Avatar, Divider, Grid } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import UserStatusDropDown from '@/components/user/UserStatusDropDown'
import { useNotification } from '@/hooks/useNotification'
import {
  ActivityLogTypeEnum,
  User,
  UserStatusEnum,
  useUpdateAvatarMutation,
  useUpdateUserStatusMutation
} from '@/@data'
import { useTranslator } from '@/hooks/useTranslator'
import { getFullName } from '@/utils/getInitials'
import { getRoleIcon, getRoleColor } from '@/utils/getRoleIcon'
import PersonalInfoForm from '@/components/user/lift/PersonalInfoForm'
import ResetPasswordForm from '@/components/user/lift/ResetPasswordForm'
import AppLogs from '@/components/common/AppLogs'

const statusBgColor: Record<string, string> = {
  active: '#D4F4E2',
  waiting: '#FFECD9',
  inactive: '#EAEAEC',
  guest: '#DFEBFF',
  blocked: '#FFDBDC',
  guest_waiting: '#DFEBFF'
}

const LiftUserDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { __ } = useTranslator()
  const n = useNotification()
  // const navigate = useNavigate()
  const [openSetting, setopenSetting] = useState(false)
  const [openResetPass, setOpenResetPass] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const updateAvatarMutation = useUpdateAvatarMutation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isOpenLogs, setIsOpenLogs] = useState(false)

  // Fetch employee data
  const { data: employee, refetch } = useQuery({
    ...User.objects.findOneQueryFactory(Number(id))
  })

  const changeStatusMutation = useUpdateUserStatusMutation()

  const formRef = useRef<{ submit: () => void }>(null)

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file && employee) {
      updateAvatarMutation.mutate(
        {
          id: employee.id,
          avatar: file
        },
        {
          onSuccess: () => {
            refetch?.()
            n.success(__('Avatar updated successfully'))
          },
          onError: error => {
            n.error(__('Failed to update avatar: ') + error.message)
          }
        }
      )
    }
  }

  if (!employee) return null

  return (
    <>
      <BoxPage
        showBackButton={true}
        title={__('Lift Detail')}
        breadcrumbs={[
          { label: __('Users Management'), href: '/cms/users/employee' },
          { label: __('Lift User Management'), href: '/cms/users/lift-user' },
          { label: __('Lift User Detail') }
        ]}
      >
        <Grid container spacing={5}>
          <Grid size={openSetting ? 10 : 12} className='left-layout'>
            <Card
              sx={{
                borderRadius: 3,
                mb: 6,
                backgroundColor: statusBgColor[employee.status?.toLowerCase()] || '#EAEAEC'
              }}
            >
              <CardContent>
                <div className=''>
                  <Box className='flex items-star justify-between mb-4'>
                    <Box sx={{ display: 'flex', gap: 3, mb: 3, width: '100%' }}>
                      <Avatar
                        src={employee.avatar_file?.tiny_url || employee.avatar}
                        alt={getFullName(employee)}
                        sx={{ width: 80, height: 80 }}
                      />

                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Typography variant='h5' gutterBottom sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}>
                            {getFullName(employee)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 3 }}>
                            <IconButton
                              className='bg-[#585164] w-[30px] h-[30px] icon-role'
                              style={{
                                backgroundColor: getRoleColor(employee.role?.name || '')
                              }}
                            >
                              <i className={`${getRoleIcon(employee.role?.name || '')} text-[#ffffff]`} />
                            </IconButton>
                          </Box>
                          <UserStatusDropDown
                            status={employee.status}
                            statusPrevious={employee.status_previous}
                            excludeGuestStatuses={true}
                            readonly={employee.role_id === 1}
                            onSelect={status => {
                              changeStatusMutation.mutate(
                                {
                                  id: [employee.id],
                                  status: status as UserStatusEnum
                                },
                                {
                                  onSuccess: () => {
                                    refetch()
                                    n.success(__('Status updated successfully'))
                                  },
                                  onError: err => {
                                    if (err.message) {
                                      n.error(__(err.message))

                                      return
                                    }

                                    n.error(__('Status update failed'))
                                  }
                                }
                              )
                            }}
                            sx={{
                              height: '30px',
                              fontSize: '13px',
                              '&.Mui-disabled': { opacity: 1 }
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} className='text-[#2F2B3D66]'>
                          <i className='tabler-id' />
                          <span className='text-[15px] leading-[22px]'>{employee.system_code}</span>
                          <i
                            className='tabler-copy'
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              navigator.clipboard.writeText(employee.system_code || '')
                              n.success(__('Copied to clipboard!'))
                            }}
                          />
                        </Box>
                        {openSetting && (
                          <Box className='flex items-center gap-2 mt-2'>
                            <Button
                              onClick={handleUploadClick}
                              variant='outlined'
                              className='gap-2 rounded-full bg-[#ffffff] w-[200px] py-[15px] text-[13px]'
                              sx={{
                                height: 30
                              }}
                            >
                              {__('Upload New Photo')}
                            </Button>
                            <input
                              type='file'
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              accept='image/*'
                              className='hidden'
                            />

                            <Typography variant='body2' className='text-[#2F2B3D66] text-[13px] flex w-full '>
                              {__('At least 800x800 px recommended JPG or PNG is allowed')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <IconButton
                        size='small'
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: '#e0e0e0'
                          }
                        }}
                        className='bg-[#ffffff]'
                        onClick={() => setopenSetting(!openSetting)}
                      >
                        <i
                          className={`tabler-${openSetting ? 'x' : 'settings'} text-[#2F2B3DE5]`}
                          style={{ fontSize: 18 }}
                        />
                      </IconButton>
                      <IconButton
                        size='small'
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: '#e0e0e0'
                          }
                        }}
                        className='bg-[#ffffff]'
                        onClick={() => setIsOpenLogs(true)}
                      >
                        <i className='tabler-history text-[#2F2B3DE5]' style={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {openResetPass ? (
                    <Card sx={{ mb: 6, p: 4, borderRadius: 3, backgroundColor: '#ffffff' }}>
                      <Typography className='text-[18px]'>{__('Reset Password')}</Typography>
                      <Divider sx={{ my: 2 }} />
                      <ResetPasswordForm
                        key={formKey}
                        ref={formRef}
                        employee={employee}
                        onSuccess={() => refetch?.()}
                      />
                    </Card>
                  ) : (
                    <>
                      <Card sx={{ mb: 6, p: 4, borderRadius: 3, backgroundColor: '#ffffff' }}>
                        <Typography className='text-[18px]'>{__('Personal Info')}</Typography>
                        <Divider sx={{ my: 2 }} />

                        {openSetting ? (
                          <PersonalInfoForm
                            key={formKey}
                            ref={formRef}
                            employee={employee}
                            onSuccess={() => refetch?.()}
                            refetch={refetch}
                            setOpenSetting={setopenSetting}
                          />
                        ) : (
                          <div className='grid gap-5'>
                            <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                              <Box className='flex flex-col'>
                                <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                  {__('Full Name')}:
                                </Typography>
                                <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                  {getFullName(employee)}
                                </Typography>
                              </Box>

                              <Box className='flex flex-col'>
                                <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                  {__('User Code')}:
                                </Typography>
                                <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                  {employee?.employee_id || '-'}
                                </Typography>
                              </Box>
                              <Box className='flex flex-col'>
                                <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                  {__('DOB')}:
                                </Typography>
                                <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                  {employee?.birth_date || '-'}
                                </Typography>
                              </Box>

                              <Box className='flex flex-col'>
                                <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                  {__('Role')}:
                                </Typography>
                                <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                  {employee?.role?.name || '-'}
                                </Typography>
                              </Box>

                              <Box className='flex flex-col'>
                                <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                  {__('Email')}:
                                </Typography>
                                <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                  {employee?.email || '-'}
                                </Typography>
                              </Box>

                              <Box className='flex flex-col'>
                                <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                  {__('Phone')}:
                                </Typography>
                                <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                  {employee?.phone_code || '-'} {employee?.phone || '-'}
                                </Typography>
                              </Box>
                            </div>
                          </div>
                        )}
                      </Card>
                      <Card sx={{ mb: 6, p: 4, borderRadius: 3, backgroundColor: '#ffffff' }}>
                        <Typography className='text-[18px]'>{__('Company Info')}</Typography>
                        <Divider sx={{ my: 2 }} />

                        <div className='grid gap-5'>
                          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                            <Box className='flex flex-col'>
                              <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                {__('Employer')}:
                              </Typography>
                              <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                {employee?.company?.name || '-'}
                              </Typography>
                            </Box>

                            <Box className='flex flex-col'>
                              <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                {__('Employer Registration')}:
                              </Typography>
                              <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                {employee?.company?.business_number || '-'}
                              </Typography>
                            </Box>

                            <Box className='flex flex-col'>
                              <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                {__('Email')}:
                              </Typography>
                              <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                {employee?.company?.email || '-'}
                              </Typography>
                            </Box>

                            <Box className='flex flex-col'>
                              <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                {__('Phone')}:
                              </Typography>
                              <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                {employee?.company?.poc_phone || '-'}
                              </Typography>
                            </Box>

                            <Box className='flex flex-col'>
                              <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                                {__('Address')}:
                              </Typography>
                              <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                                {employee?.company?.address || '-'}
                              </Typography>
                            </Box>
                          </div>
                        </div>
                      </Card>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </Grid>

          {openSetting && (
            <Grid size={2} className='right-layout'>
              <Card
                sx={{
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  p: 6
                }}
              >
                <Button
                  variant='contained'
                  className={`text-[15px] font-medium leading-[22px] ${openResetPass ? 'bg-[#6F83F629] text-[#6F83F6]' : 'bg-[#3551F2] text-[#fffff]'} shadow-none rounded-full`}
                  startIcon={openResetPass ? <i className='' /> : <i className='tabler-lock-access' />}
                  onClick={() => setOpenResetPass(!openResetPass)}
                >
                  {openResetPass ? __('Back To Detail') : __('Reset Password')}
                </Button>
                <Button
                  variant='contained'
                  className='text-[15px] font-medium leading-[22px] bg-[#6F83F629] text-[#6F83F6] shadow-none rounded-full'
                  onClick={() => setFormKey(prev => prev + 1)}
                >
                  {__('Reset')}
                </Button>
                <Button
                  variant='contained'
                  type='submit'
                  className='text-[#28C76F] text-[15px] font-medium leading-[22px] bg-[#28C76F29] shadow-none rounded-full'
                  onClick={() => formRef.current?.submit()}
                >
                  {__('Save')}
                </Button>
              </Card>
            </Grid>
          )}
        </Grid>
      </BoxPage>

      <AppLogs
        open={isOpenLogs}
        setOpen={setIsOpenLogs}
        title={__('History Menu Lift User Detail')}
        object_type={ActivityLogTypeEnum.User}
        objectId={employee.id}
      />
    </>
  )
}

export default LiftUserDetail
